import React, { FunctionComponent, useContext, useMemo } from 'react';
import { CalendarContext } from '../../contexts';
import { DefaultWeekdayLabel, WeekdayLabelProps } from '../../Components';
import { useLocaleFirstDayOfWeekIndex } from '../useLocaleFirstDayOfWeekIndex';

/**
 * This hook will render and return the WeekdayHeadings in the locale's order e.g. Monday first for en-GB and Sunday first for en-US
 * */
export const useWeekdayLabels = ({
    WeekdayLabel = DefaultWeekdayLabel,
}: {
    WeekdayLabel?: FunctionComponent<WeekdayLabelProps>;
}) => {
    const { locale } = useContext(CalendarContext);
    const localeFirstDayOfWeek = useLocaleFirstDayOfWeekIndex();

    const dayOfWeekLabels = useMemo(() => {
        const formatLong = (date: Date) =>
            new Intl.DateTimeFormat(locale, { timeZone: 'UTC', weekday: 'long' }).format(date);
        const formatNarrow = (date: Date) =>
            new Intl.DateTimeFormat(locale, { timeZone: 'UTC', weekday: 'narrow' }).format(date);
        const formatShort = (date: Date) =>
            new Intl.DateTimeFormat(locale, { timeZone: 'UTC', weekday: 'short' }).format(date);

        return new Array(7).fill(null).map((_, index) => {
            const date = new Date(Date.UTC(1970, 1, 1 + index));
            const isFirst = index === (0 + localeFirstDayOfWeek) % 7;
            const isLast = index === (6 + localeFirstDayOfWeek) % 7;

            return (
                <WeekdayLabel
                    isFirst={isFirst}
                    isLast={isLast}
                    dayOfWeekIndex={index}
                    long={formatLong(date)}
                    narrow={formatNarrow(date)}
                    short={formatShort(date)}
                />
            );
        });
    }, [locale, WeekdayLabel, localeFirstDayOfWeek]);

    const sortedDayOfWeekLabels = useMemo(() => {
        return [
            dayOfWeekLabels[(0 + localeFirstDayOfWeek) % 7],
            dayOfWeekLabels[(1 + localeFirstDayOfWeek) % 7],
            dayOfWeekLabels[(2 + localeFirstDayOfWeek) % 7],
            dayOfWeekLabels[(3 + localeFirstDayOfWeek) % 7],
            dayOfWeekLabels[(4 + localeFirstDayOfWeek) % 7],
            dayOfWeekLabels[(5 + localeFirstDayOfWeek) % 7],
            dayOfWeekLabels[(6 + localeFirstDayOfWeek) % 7],
        ];
    }, [dayOfWeekLabels, localeFirstDayOfWeek]);

    return sortedDayOfWeekLabels;
};
